export const NO_MORE_ITEMS_ERROR = 'NoMoreItemsError';
export const NOT_FOUND_ERROR = 'NotFoundError';
export const NO_LABELED_VERSION_ERROR = 'NoLabeledVersionError';

export class NoMoreItemsError extends Error {
  constructor() {
    super('No more items');
    this.name = NO_MORE_ITEMS_ERROR;
  }
}

export function isNoMoreItemsError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === NO_MORE_ITEMS_ERROR;
  }
  return false;
}

export class NotFoundError extends Error {
  constructor() {
    super('Not found');
    this.name = NOT_FOUND_ERROR;
  }
}

/**
 * The case has no pass by that person. In the review viewer this means the case is not a review case: a reviewer
 * of a task only gets review cases, but next-item first returns a case the person still holds to label.
 */
export class NoLabeledVersionError extends Error {
  constructor() {
    super('Could not find a labeled version for this user');
    this.name = NO_LABELED_VERSION_ERROR;
  }
}

export function isNoLabeledVersionError(error: unknown): boolean {
  return error instanceof Error && error.name === NO_LABELED_VERSION_ERROR;
}

/**
 * What the backend said about a refused request. A JSON object is read for the first that says something, in
 * order: the messages of its `violations` (`{"violations": [{"field", "message"}]}`, those without a message
 * skipped), Spring's ProblemDetail `detail`, a `message`, the default error body's `error`; an object with none of
 * them says nothing. Any other body, a JSON scalar or array included, is the backend's plain text
 * ("Item[…] not labelable by …").
 */
export async function responseDetail(response: Response): Promise<string> {
  let text = '';
  try {
    text = (await response.text()).trim();
  } catch {
    return '';
  }
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return text;
  }
  // a JSON scalar or array is not a refusal body: show it as the text it is
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return text;
  }
  const { violations, detail, message, error } = body as Record<string, unknown>;
  const said = Array.isArray(violations)
    ? violations
        .filter(violation => typeof violation?.message === 'string' && violation.message !== '')
        .map(({ field, message }: { field?: string; message: string }) =>
          field?.trim() ? `${field}: ${message}` : message
        )
        .join(', ')
    : '';
  return (
    [said, detail, message, error].find(
      (value): value is string => typeof value === 'string' && value !== ''
    ) ?? ''
  );
}

/** The error of a refused request: its status, then what the backend said ({@link responseDetail}). */
export async function responseError(response: Response): Promise<Error> {
  const detail = await responseDetail(response);
  const status = [response.status, response.statusText].filter(Boolean).join(' ');
  return new Error(detail ? `${status}: ${detail}` : status);
}
